import React, { Children, useCallback, useEffect, useRef, useState } from 'react';
import ChevronRightIcon from '~/assets/images/icons/chevron-right.svg';
import { Button, ThemeUIStyleObject } from 'theme-ui';

type Props = {
  slides: React.ReactNode[];
  autoplaySpeed?: number;
  transitionSpeed?: number;
  transitionDelay?: number;
  arrows?: boolean;
  dots?: boolean;
  swipeControls?: boolean;
  forwardSx?: ThemeUIStyleObject;
};

/**
 * A very light weight carousel that can render without JS.
 * (All images except for first are set to opacity:0 though).
 *  JS enabled allows go to next image functionality.
 * Pass in a @param slideId matching the key for each child slide inserted
 * Passes @param isActiveSlide to children slides
 */

const LiteCarousel = ({
  slides,
  autoplaySpeed,
  transitionSpeed,
  transitionDelay,
  arrows = true,
  dots = true,
  swipeControls = true,
  forwardSx = {},
}: Props) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const snapTo = useCallback(
    (value = 0) => {
      let carouselMaxLength = slides.length;
      let newIndex = value;

      if (newIndex >= carouselMaxLength) newIndex = 0;
      if (newIndex < 0) newIndex = carouselMaxLength - 1;

      setCarouselIndex(newIndex);
    },
    [slides.length]
  );

  useEffect(() => {
    if (!carouselRef.current) return;
    let timer = null;
    if (autoplaySpeed) {
      timer = setTimeout(() => {
        snapTo(carouselIndex + 1);
      }, autoplaySpeed);
    }

    return () => {
      if (timer) return clearTimeout(timer);
    };
  }, [autoplaySpeed, carouselIndex, snapTo]);

  const checkDirection = useCallback(
    (xEnd, xStart) => {
      let currentIndex = 0;
      setCarouselIndex((state) => {
        currentIndex = state;
        return state;
      });

      if (xEnd < xStart) snapTo(currentIndex + 1);
      if (xEnd > xStart) snapTo(currentIndex - 1);
    },
    [snapTo]
  );

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || !swipeControls) return;

    let touchStartX = 0;
    let touchEndX = 0;
    let touchStartTime = 0;
    let initialTouchCount = 0;

    const touchStartControl = (e: TouchEvent) => {
      // Store initial number of touch points
      if (e.touches) {
        initialTouchCount = e.touches.length;
        // Don't track swipes if multiple touches detected (pinch gesture)
        if (initialTouchCount > 1) return;
      }

      touchStartX = e?.changedTouches?.[0]?.screenX ?? e.touches[0].screenX;
      touchStartTime = Date.now();
    };

    const touchEndControl = (e: TouchEvent) => {
      // Don't process if initial touch was multi-finger
      if (initialTouchCount > 1) return;

      // For touch events, check if ending with multiple fingers
      if (e.touches && e.touches.length > 1) return;

      const touchEndTime = Date.now();
      touchEndX = e?.changedTouches?.[0]?.screenX ?? e.touches[0].screenX;

      // Only process if the gesture was quick enough (not a slow zoom gesture)
      if (touchEndTime - touchStartTime < 300) {
        checkDirection(touchEndX, touchStartX);
      }
    };

    carousel.addEventListener('touchstart', touchStartControl);
    carousel.addEventListener('touchend', touchEndControl);

    return () => {
      carousel.removeEventListener('touchstart', touchStartControl);
      carousel.removeEventListener('touchend', touchEndControl);
    };
  }, [carouselRef, checkDirection, swipeControls]);

  const arrayChildren = Children.toArray(slides);

  return (
    <section
      aria-label="carousel"
      sx={{
        width: '100vw',
        maxWidth: '100%',
        position: 'relative',
        height: '100%',
        isolation: 'isolate',
        ...forwardSx,
      }}
    >
      {arrows && (
        <div
          className="arrows"
          sx={{
            position: 'absolute',
            bottom: '2rem',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '34.2rem',
            height: '2rem',
            zIndex: 3,
            display: 'flex',
            justifyContent: 'space-between',
            WebkitScrollBar: {
              display: 'none',
            },
            WebkitTapHighlightColor: 'transparent',
            scrollbarWidth: 'none',
          }}
        >
          <Button
            variant="bare"
            aria-label="navigate to previous slide"
            sx={{
              transform: 'rotate(180deg)',
              svg: { height: '2rem', width: '2rem', path: { stroke: 'white' } },
            }}
            onClick={() => snapTo(carouselIndex - 1)}
          >
            <ChevronRightIcon />
          </Button>
          <Button
            variant="bare"
            aria-label="navigate to next slide"
            sx={{
              transform: 'rotate(0deg)',
              svg: { height: '2rem', width: '2rem', path: { stroke: 'white' } },
            }}
            onClick={() => snapTo(carouselIndex + 1)}
          >
            <ChevronRightIcon />
          </Button>
        </div>
      )}
      {dots && (
        <ul
          className="dots-list"
          sx={{
            position: 'absolute',
            bottom: '2rem',
            height: '2rem',
            width: '100%',
            zIndex: 2,
            margin: 0,
            padding: 0,
            display: 'flex',
            gap: '8px',
            listStyle: 'none',
            justifyContent: 'center',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {Children.map(arrayChildren, (child, i) => {
            if (!React.isValidElement(child)) return null;
            return (
              <li key={`${child.props.slideId}-${i}`}>
                <button
                  aria-current={i === carouselIndex}
                  aria-label={`go to slide ${i + 1}`}
                  onClick={() => snapTo(i)}
                  sx={{
                    appearance: 'none',
                    border: 0,
                    opacity: i === carouselIndex ? 1 : 0.2,
                    margin: 0,
                    padding: 0,
                    height: '8px',
                    width: '8px',
                    borderRadius: '100%',
                    background: 'white',
                    cursor: 'pointer',
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}
      <div
        ref={carouselRef}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '2rem',
          height: '100%',
          overflow: 'hidden',
          touchAction: 'manipulation',
        }}
      >
        {Children.map(arrayChildren, (child, i) => {
          if (!React.isValidElement(child)) return null;
          let isCurrentSlide = carouselIndex === i;
          let prevSlide = carouselIndex === (i === 0 ? arrayChildren.length - 1 : i - 1);
          let nextSlide = carouselIndex === (i === arrayChildren.length - 1 ? 0 : i + 1);
          if (!isCurrentSlide && !prevSlide && !nextSlide) return null;
          return (
            <div
              key={`${child.props.slideId}-${i}`}
              aria-label={`slide ${i + 1} out of ${slides.length}`}
              aria-hidden={isCurrentSlide ? 'false' : 'true'}
              tabIndex={isCurrentSlide ? 0 : -1}
              sx={{
                flexGrow: 1,
                flexShrink: 0,
                height: '100%',
                width: '100%',
                position: i === 0 ? 'relative' : 'absolute',
                scrollSnapAlign: 'start',
                opacity: isCurrentSlide ? 1 : 0,
                pointerEvents: isCurrentSlide ? 'auto' : 'none',
                transition: `opacity  ${transitionSpeed ?? 1}s ease-in-out`,
                transitionDelay: `${transitionDelay ?? 0.5}s ease-in-out`,
              }}
            >
              {React.cloneElement(child)}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default LiteCarousel;
